import React from 'react';
import PropTypes from 'prop-types';

import { brandRamp } from '../../bundles/MarketingInsights/styles/chartTokens';
import { buildRadar, RADAR_SIZE } from './buyerLocationProjection';

// Stylized radar: venue at center, dashed distance rings, one dot per city
// sized by ticket volume and colored by density. All coordinates are clamped
// to the viewBox by buildRadar, so a far-away city can never overflow.
const BuyerLocationMap = ({ venue, cities, radiusMi, pctWithinRadius }) => {
  const { size, center, usableRadius, ringPx, points } = buildRadar({
    venue,
    cities,
    radiusMi,
  });

  const ramp = brandRamp();
  const maxTickets = cities.reduce((m, c) => Math.max(m, c.tickets), 1);
  const colorFor = (tickets) => {
    // Denser cities get the darker brand color (ramp[0] = darkest).
    const idx = Math.min(
      ramp.length - 1,
      Math.floor((1 - tickets / maxTickets) * (ramp.length - 1)),
    );
    return ramp[idx];
  };

  return (
    <div className="mi-buyer-loc-map">
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="mi-buyer-loc-map-svg"
        role="img"
        aria-label={`Map of buyer locations around ${venue.name || 'the venue'}`}
      >
        <circle cx={center} cy={center} r={usableRadius} className="mi-buyer-loc-field" />
        <circle cx={center} cy={center} r={ringPx} className="mi-buyer-loc-ring" />
        <circle cx={center} cy={center} r={ringPx / 2} className="mi-buyer-loc-ring is-inner" />

        {points.map((p) => (
          <circle
            key={`${p.city}-${p.state}`}
            cx={p.x}
            cy={p.y}
            r={p.r}
            fill={colorFor(p.tickets)}
            className="mi-buyer-loc-dot"
          >
            <title>{`${p.city}, ${p.state} · ${p.tickets} tickets`}</title>
          </circle>
        ))}

        <circle cx={center} cy={center} r={5} className="mi-buyer-loc-venue" />
      </svg>

      <div className="mi-buyer-loc-callout">
        <span className="mi-buyer-loc-callout-pct">{pctWithinRadius}%</span>
        <span className="mi-buyer-loc-callout-label">within {radiusMi} mi</span>
      </div>

      <div className="mi-buyer-loc-legend">
        <span className="mi-buyer-loc-legend-venue" aria-hidden="true" /> Venue
        <span className="mi-buyer-loc-legend-ring" aria-hidden="true" /> {Math.round(radiusMi / 2)} / {radiusMi} mi
      </div>
    </div>
  );
};

BuyerLocationMap.propTypes = {
  venue: PropTypes.shape({
    name: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
  }).isRequired,
  cities: PropTypes.arrayOf(
    PropTypes.shape({
      city: PropTypes.string,
      state: PropTypes.string,
      tickets: PropTypes.number,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
      within_radius: PropTypes.bool,
    }),
  ).isRequired,
  radiusMi: PropTypes.number.isRequired,
  pctWithinRadius: PropTypes.number.isRequired,
};

export { RADAR_SIZE };
export default BuyerLocationMap;
